/**
 * MediaPipe Face Landmarker adapter (pipeline stage 3).
 *
 * This is the only file in the browser host that knows MediaPipe exists. It returns the
 * core package's `FaceLandmarks`, so everything downstream is model-agnostic and swapping
 * the detector touches nothing but this file.
 *
 * Three deliberate departures from the obvious implementation:
 *
 * 1. **Assets are local.** The WASM runtime is copied out of the installed npm package and
 *    the model is vendored by `npm run fetch:models`; neither is fetched from a CDN at
 *    runtime. A monitoring agent that calls out to third-party hosts when the camera starts
 *    cannot be deployed offline or air-gapped, and hands a supply-chain dependency to
 *    whoever controls that host.
 * 2. **The delegate falls back.** `auto` tries GPU and drops to CPU if WebGL is unavailable
 *    or initialisation throws, because a machine without a usable GPU path must still work
 *    rather than showing the employee an error.
 * 3. **More than one face is detected.** `numFaces` comes from policy and is at least two,
 *    which is what makes "someone else is in frame" observable. With a single-face detector
 *    a colleague's head movements would be silently attributed to the employee.
 */

import {
  FaceLandmarker,
  FilesetResolver,
  type FaceLandmarkerResult,
  type NormalizedLandmark,
} from '@mediapipe/tasks-vision';
import type { FaceLandmarks, VisionPolicy } from '../core/index.js';

export interface LandmarkerHandle {
  readonly detector: FaceLandmarker;
  readonly delegate: 'GPU' | 'CPU';
  readonly modelVersion: string;
  close(): void;
}

export class ModelAssetError extends Error {}

/**
 * Create a landmarker, honouring the policy's delegate preference with a CPU fallback.
 *
 * Errors are deliberately actionable: the most common failure in development is forgetting
 * to vendor the assets, and "fetch failed" is a much worse message than an instruction.
 */
export async function createFaceLandmarker(vision: VisionPolicy): Promise<LandmarkerHandle> {
  let fileset: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>;
  try {
    fileset = await FilesetResolver.forVisionTasks(vision.wasmBasePath);
  } catch (cause) {
    throw new ModelAssetError(
      `Could not load the MediaPipe WASM runtime from ${vision.wasmBasePath}. ` +
        'Run `npm run fetch:models` from the repository root to vendor the runtime and the ' +
        'face model, then reload. The agent never downloads these at runtime.',
      { cause },
    );
  }

  // Probe the model URL first. MediaPipe surfaces a load failure as an opaque `[object Event]`,
  // which tells nobody anything — a 404 from a wrong base path and a corrupt download look
  // identical. One HEAD request turns that into an actionable message.
  const probe = await probeAsset(vision.modelAssetPath);
  if (!probe.ok) {
    throw new ModelAssetError(
      `Face model not reachable at ${absoluteUrl(vision.modelAssetPath)} (${probe.detail}). ` +
        'If the status is 404, the path is wrong for this app\'s base URL; if the file is ' +
        'missing, run `node work-focus/scripts/fetch-models.mjs` to vendor it.',
    );
  }

  const attempts: Array<'GPU' | 'CPU'> =
    vision.delegate === 'cpu' ? ['CPU'] : vision.delegate === 'gpu' ? ['GPU'] : ['GPU', 'CPU'];

  let lastError: unknown;
  for (const delegate of attempts) {
    try {
      const detector = await FaceLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: vision.modelAssetPath, delegate },
        runningMode: 'VIDEO',
        // At least two, so a bystander is observable rather than invisible.
        numFaces: Math.max(2, vision.maxFaces),
        minFaceDetectionConfidence: vision.minDetectionConfidence,
        minFacePresenceConfidence: vision.minLandmarkConfidence,
        minTrackingConfidence: vision.minLandmarkConfidence,
        outputFaceBlendshapes: vision.outputBlendshapes,
        // Not requested: the pose comes from our own verified solver against the canonical
        // model, not from MediaPipe's transformation matrix, whose coordinate convention
        // cannot be validated without a camera and a human head.
        outputFacialTransformationMatrixes: false,
      });

      return {
        detector,
        delegate,
        modelVersion: `mediapipe-face-landmarker/${basename(vision.modelAssetPath)}`,
        close: () => {
          detector.close();
        },
      };
    } catch (error) {
      lastError = error;
      // Fall through to the next delegate; a GPU failure is expected on some machines.
    }
  }

  throw new ModelAssetError(
    `Could not initialise the face landmarker from ${vision.modelAssetPath}. ` +
      'Check that `npm run fetch:models` has been run and that the model file is served. ' +
      `Last error: ${lastError instanceof Error ? lastError.message : String(lastError)}`,
    { cause: lastError },
  );
}

export interface DetectionResult {
  readonly landmarks: FaceLandmarks | null;
  /**
   * 0..1 smile strength from blendshapes, or null when blendshapes are disabled or absent.
   *
   * Null is meaningfully different from 0: it means "not measured", and the liveness verifier
   * fails an unmeasurable challenge rather than passing it.
   */
  readonly smileScore: number | null;
}

/**
 * Run detection for one video frame.
 *
 * `timestampMs` must increase monotonically — VIDEO mode uses it to track between frames,
 * and a repeated or regressing timestamp throws. The caller's monotonic clock satisfies
 * this, but the guard is cheap and a stalled camera should not take the loop down.
 */
export function detectLandmarks(
  handle: LandmarkerHandle,
  video: HTMLVideoElement,
  timestampMs: number,
  presenceConfidence: number,
  streamIsMirrored = false,
): DetectionResult {
  let result: FaceLandmarkerResult;
  try {
    result = handle.detector.detectForVideo(video, Math.round(timestampMs));
  } catch {
    // A single failed inference is a dropped frame, not a fatal condition.
    return { landmarks: null, smileScore: null };
  }

  const faces = result.faceLandmarks ?? [];
  if (faces.length === 0) return { landmarks: null, smileScore: null };

  const primary = largestFace(faces);
  const landmarks: FaceLandmarks = {
    // Un-mirror here, once, so the core always works on a canonical frame and no downstream
    // stage needs a special case. Flipping x also flips the sign of yaw and roll, which is
    // exactly the correction a pre-mirrored stream needs.
    points: primary.map(
      (point) => [streamIsMirrored ? 1 - point.x : point.x, point.y, point.z ?? 0] as const,
    ),
    imageWidth: video.videoWidth,
    imageHeight: video.videoHeight,
    detectionConfidence: presenceConfidence,
    // The true count, so the pipeline can refuse to attribute orientation.
    faceCount: faces.length,
  };

  return { landmarks, smileScore: smileFromBlendshapes(result) };
}

/**
 * Average of the left and right smile blendshapes.
 *
 * Averaging the two sides rather than taking the maximum means a one-sided expression — or a
 * face lit unevenly so that only one side scores — does not satisfy the challenge on its own.
 */
function smileFromBlendshapes(result: FaceLandmarkerResult): number | null {
  const categories = result.faceBlendshapes?.[0]?.categories;
  if (categories === undefined || categories.length === 0) return null;

  let left: number | null = null;
  let right: number | null = null;
  for (const category of categories) {
    if (category.categoryName === 'mouthSmileLeft') left = category.score;
    else if (category.categoryName === 'mouthSmileRight') right = category.score;
  }
  if (left === null && right === null) return null;
  const present = [left, right].filter((v): v is number => v !== null);
  return present.reduce((total, value) => total + value, 0) / present.length;
}

/**
 * Pick the face occupying the most vertical space (nearest the camera).
 *
 * Selecting a primary face is only for *measurement*; it never decides attribution. When
 * more than one face is present the quality assessor flags it and the state engine reports
 * `unknown`, so this choice cannot silently attribute a bystander's movement to the employee.
 */
function largestFace(faces: readonly NormalizedLandmark[][]): NormalizedLandmark[] {
  let best = faces[0] as NormalizedLandmark[];
  let bestExtent = -1;
  for (const face of faces) {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (const point of face) {
      if (point.y < min) min = point.y;
      if (point.y > max) max = point.y;
    }
    const extent = max - min;
    if (extent > bestExtent) {
      bestExtent = extent;
      best = face;
    }
  }
  return best;
}

function basename(path: string): string {
  const parts = path.split('/');
  return parts[parts.length - 1] ?? path;
}

function absoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (typeof window === 'undefined') return path;
  return new URL(path, window.location.origin).toString();
}

/** HEAD the asset so a load failure reports a status code rather than an opaque event. */
async function probeAsset(path: string): Promise<{ ok: boolean; detail: string }> {
  try {
    const response = await fetch(absoluteUrl(path), { method: 'HEAD' });
    if (!response.ok) return { ok: false, detail: `HTTP ${response.status}` };
    const length = Number(response.headers.get('content-length') ?? '0');
    // A wrong path often returns the SPA's index.html with a 200, which is far smaller than
    // the multi-megabyte model and would otherwise fail deep inside MediaPipe.
    if (length > 0 && length < 1_000_000) {
      return {
        ok: false,
        detail: `served ${length} bytes, too small to be the model — the path is probably ` +
          'falling through to the app shell',
      };
    }
    return { ok: true, detail: 'ok' };
  } catch (error) {
    return {
      ok: false,
      detail: error instanceof Error ? error.message : 'request failed',
    };
  }
}
